import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
// Vuetify
import 'vuetify/styles'
import { createVuetify } from 'vuetify'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'

import { aliases, mdi } from 'vuetify/iconsets/mdi'

import 'vuetify/styles'
import '@mdi/font/css/materialdesignicons.css'
import 'driver.js/dist/driver.css'
import './styles/tableConfig.css'

import store from './store/index'
import { onAuthStateChanged, setPersistence, browserSessionPersistence, signOut } from 'firebase/auth'
import { auth } from '@/config/firebase'
import { applyTheme } from '@/utils/theme'
import { registerServiceWorker } from '@/registerServiceWorker'

const icons = {
    defaultSet: 'mdi',
    aliases,
    sets: {
        mdi,
    }
}

const vuetify = createVuetify({
    components,
    directives,
    icons,
})

applyTheme();

let app;
const SESSION_KEY = 'userAuthContext';
const LOGIN_KEYS = ['sessionExpiresAtMs', 'justLoggedInWithSSO', 'userEmail'];

const clearAuthArtifacts = () => {
    sessionStorage.removeItem(SESSION_KEY);
    LOGIN_KEYS.forEach((key) => localStorage.removeItem(key));
};

const syncCompanyFromRoute = async (retry = 0) => {
    const slug = router?.currentRoute?.value?.params?.company;
    if (!slug) return;
    if (store?.state?.company?.slug === slug) return;
    try {
        const ok = await store.dispatch('setCompany', slug);
        if (!ok && retry < 5) {
            setTimeout(() => syncCompanyFromRoute(retry + 1), 300);
        }
    } catch (error) {
        console.error('Falha ao sincronizar empresa na sessão', error);
        if (retry < 5) {
            setTimeout(() => syncCompanyFromRoute(retry + 1), 300);
        }
    }
};

const ensureSessionPersistence = async () => {
    try {
        await setPersistence(auth, browserSessionPersistence);
    } catch (error) {
        console.error('Falha ao definir persistência de sessão', error);
    }
};

const detectSessionMismatch = async (user) => {
    if (!user) {
        clearAuthArtifacts();
        return null;
    }
    const currentContext = `${user.uid}|${navigator.userAgent}`;
    const storedContext = sessionStorage.getItem(SESSION_KEY);
    if (storedContext && storedContext !== currentContext) {
        try {
            await signOut(auth);
        } catch (error) {
            console.error('Erro ao desconectar sessão conflitante', error);
        }
        clearAuthArtifacts();
        return null;
    }
    sessionStorage.setItem(SESSION_KEY, currentContext);
    return currentContext;
};

const startApp = () => {
    if (!navigator.webdriver) {
        registerServiceWorker();
    }

    const ensureAppMounted = async () => {
        if (app) return;
        app = createApp(App)
            .use(router)
            .use(vuetify)
            .use(store)
            .mount('#app')
        await router.isReady();
        await syncCompanyFromRoute();
    };

    ensureAppMounted().catch((error) => {
        console.error('Falha ao montar aplicação', error);
    });

    onAuthStateChanged(auth, async (user) => {
        if (user) {
            const sessionContext = await detectSessionMismatch(user);
            if (!sessionContext || !auth.currentUser) {
                store.commit('SET_USER', null);
                store.commit('CLEAR_EMAIL');
                clearAuthArtifacts();
            } else {
            store.commit('SET_USER', user);
            if (user.email) store.commit('SET_EMAIL', user.email);
            }
        } else {
            store.commit('SET_USER', null);
            store.commit('CLEAR_EMAIL');
            clearAuthArtifacts();
        }

        if (user) {
            await router.isReady();
            await syncCompanyFromRoute();
        }
    });
};

ensureSessionPersistence().finally(() => {
    startApp();
});

router.afterEach(async () => {
    await syncCompanyFromRoute();
});
